import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { formatByteSize, formatDate } from '@xdocs/i18n';
import { Badge, EmptyState, Modal, Skeleton, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { ExplorerEntryIcon } from './fileIcons.js';
import { useExplorerFileEntry } from './useExplorerFileEntry.js';
import { useExplorerT } from './useExplorerT.js';
import styles from './FilePreviewPanel.module.css';

export interface FilePreviewPanelProps {
  fileId: string;
  onClose: () => void;
}

const STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'neutral',
  uploaded: 'info',
  ingesting: 'info',
  ready: 'success',
  failed: 'error',
};

/**
 * F7 slide-in preview pane (also the content of `/explorer/preview/:fileId`).
 * No `<Preview>` registry component exists in `@xdocs/ui` yet (F32 is
 * WS-MEDIA's deliverable, not this task's) — every kind degrades to the
 * generic icon + type-label body per F32's own documented AC ("renderer
 * failure degrades to generic + type label"); an `image`/`book-cover`
 * descriptor with a `posterUrl` gets an actual `<img>`, everything else the
 * degraded body. Download is a documented OPEN ISSUE: `ExplorerFileEntry`
 * carries no S3 key/presigned URL in the frozen contract, so there is no
 * link to wire yet.
 */
export function FilePreviewPanel({ fileId, onClose }: FilePreviewPanelProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useExplorerT();
  const { entry, isLoading, isError, refetch } = useExplorerFileEntry(fileId);
  const [imageFailed, setImageFailed] = useState(false);

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div aria-busy="true">
        <Skeleton shape="card" />
      </div>
    );
  } else if (isError) {
    body = <EmptyState kind="error" title={t('explorer:error.title')} action={{ label: t('explorer:action.cancel'), onClick: refetch }} />;
  } else if (!entry || entry.type !== 'file') {
    body = <EmptyState kind="error" title={t('explorer:error.title')} action={{ label: t('explorer:action.cancel'), onClick: onClose }} />;
  } else {
    const previewImageUrl =
      !imageFailed && (entry.preview?.kind === 'image' || entry.preview?.kind === 'book-cover') && entry.preview.posterUrl
        ? entry.preview.posterUrl
        : null;

    body = (
      <>
        <Badge tone={STATUS_TONE[entry.status] ?? 'neutral'} className={cn(styles['statusBadge'])}>
          {t(`explorer:status.${entry.status}` as Parameters<typeof t>[0])}
        </Badge>
        <div className={cn(styles['previewBody'])}>
          {entry.status === 'ingesting' || entry.status === 'pending' ? (
            <p className={cn(styles['previewNote'])}>{t('explorer:preview.stillProcessing')}</p>
          ) : previewImageUrl ? (
            <img src={previewImageUrl} alt="" className={cn(styles['previewImage'])} onError={() => setImageFailed(true)} />
          ) : (
            <div className={cn(styles['previewFallback'])}>
              <ExplorerEntryIcon entry={entry} size={40} />
              <p className={cn(styles['previewFallbackLabel'])}>{t('explorer:preview.unavailable.title')}</p>
              <p className={cn(styles['previewFallbackHint'])}>{t('explorer:preview.unavailable.description')}</p>
            </div>
          )}
        </div>

        <p className={cn(styles['sectionLabel'])}>{t('explorer:preview.details')}</p>
        <dl className={cn(styles['detailsList'])}>
          <div className={cn(styles['detailRow'])}>
            <dt>{t('explorer:preview.meta.type')}</dt>
            <dd>{t(`explorer:kind.${entry.kind}` as Parameters<typeof t>[0])}</dd>
          </div>
          <div className={cn(styles['detailRow'])}>
            <dt>{t('explorer:preview.meta.size')}</dt>
            <dd>{formatByteSize(locale, entry.byteSize)}</dd>
          </div>
          <div className={cn(styles['detailRow'])}>
            <dt>{t('explorer:preview.meta.updated')}</dt>
            <dd>{formatDate(locale, entry.updatedAt, { dateStyle: 'medium', timeStyle: 'short' })}</dd>
          </div>
          <div className={cn(styles['detailRow'])}>
            <dt>{t('explorer:preview.meta.provenance')}</dt>
            <dd>{t(`explorer:provenance.${entry.provenance}` as Parameters<typeof t>[0])}</dd>
          </div>
        </dl>

        {entry.status === 'failed' && entry.errorMessage && (
          <p role="alert" className={cn(styles['errorNote'])}>
            {entry.errorMessage}
          </p>
        )}

        <p className={cn(styles['downloadNote'])}>{t('explorer:preview.downloadUnavailable')}</p>
      </>
    );
  }

  const title = entry && entry.type === 'file' ? entry.name : t('explorer:preview.title');

  return (
    <Modal open onClose={onClose} title={title} size="md" scrollableBody>
      {body}
    </Modal>
  );
}
